import { GraphicPrimitive } from './GraphicPrimitive';

export class Square extends GraphicPrimitive {
  constructor(x, y, size, color, fillColor, fillType, spacing, angle, segmentLength) {
    super(color, fillColor, fillType, spacing, angle, segmentLength, 'simple', 'Square');
    this.x = x;
    this.y = y;
    this.size = size;
    this.m_isDrawing = false;
    this.m_hasSecondPoint = false;
    this.regenerateStitches();
  }

  toJSON() {
    return {
      ...super.toJSON(),
      x: this.x,
      y: this.y,
      size: this.size
    };
  }

  getControlPoints() {
    return [
      { x: this.x, y: this.y },
      { x: this.x + this.size, y: this.y + this.size }
    ];
  }

  updateControlPoint(pointIndex, newX, newY) {
    if (pointIndex === 0) {
      // Bottom-right corner remains fixed
      const bottomRightX = this.x + this.size;
      const bottomRightY = this.y + this.size;

      // Calculate the size as the max delta
      const size = Math.max(Math.abs(bottomRightX - newX), Math.abs(bottomRightY - newY));

      // Determine new top-left x and y based on the dragged point relative to the fixed bottom-right
      // Ensures dragging "crossing over" flips the square correctly
      this.x = newX < bottomRightX ? bottomRightX - size : bottomRightX;
      this.y = newY < bottomRightY ? bottomRightY - size : bottomRightY;

      // Update size to calculated size
      this.size = size;

      // If dragged beyond bottom-right, flip the square accordingly
      if (newX > bottomRightX) this.x = bottomRightX;
      if (newY > bottomRightY) this.y = bottomRightY;
    } else if (pointIndex === 1) {
      // Moving bottom-right corner
      const dx = newX - this.x;
      const dy = newY - this.y;
      this.size = Math.max(Math.abs(dx), Math.abs(dy));
      // Adjust x and y if the new point makes the square extend left or up from the original top-left
      if (dx < 0) this.x = newX;
      if (dy < 0) this.y = newY;
    }
    this.regenerateStitches();
  }

  move(dx, dy) {
    this.x += dx;
    this.y += dy;
    this.regenerateStitches();
  }

  // Render the square on the canvas
  renderDrawing(ctx, transform, globalStitchIndex, stitchOffset) {
    // Don't render until both points are set
    if (!this.m_hasSecondPoint) return;
    
    const bounds = {
      minX: this.x,
      maxX: this.x + this.size,
      minY: this.y,
      maxY: this.y + this.size
    };

    this.drawFill(ctx, transform, () => {
      ctx.rect(this.x, this.y, this.size, this.size);
    }, bounds, globalStitchIndex, stitchOffset);

    ctx.save();
    ctx.beginPath();
    ctx.rect(this.x, this.y, this.size, this.size);
    // If the object is selected, draw it in red, otherwise use its original color
    ctx.strokeStyle = this.isSelected ? 'red' : this.color;
    // Make the selected line thicker for better visibility
    ctx.lineWidth = this.isSelected ? (2 / transform.scale) : (1 / transform.scale);
    ctx.stroke();
    ctx.restore();

    this.drawStitches(ctx, transform, globalStitchIndex, stitchOffset);
  }

  toPolygon() {
    // Return square as a MultiPolygon: [[[ [x1,y1], [x2,y1], [x2,y2], [x1,y2], [x1,y1] ]]]
    const x1 = this.x;
    const y1 = this.y;
    const x2 = this.x + this.size;
    const y2 = this.y + this.size;

    return [[
      [
        [x1, y1],
        [x2, y1],
        [x2, y2],
        [x1, y2],
        [x1, y1] // Explicitly close the polygon
      ]
    ]];
  }

  regenerateStitches() {
    const multiPoly = this.toPolygon();
    const parallelStitches = [];

    if (this.fillType === 'parallel' && this.fillColor) {
      const bounds = {
        minX: this.x,
        maxX: this.x + this.size,
        minY: this.y,
        maxY: this.y + this.size
      };

      this.generateParallelStitches(bounds, multiPoly);
      if (this.stitchLists.length > 0) {
        this.stitchLists.forEach(list => {
          list.color = this.fillColor;
          parallelStitches.push(list);
        });
      }
    }

    const contourStitches = this.generateContourStitches(multiPoly);
    this.stitchLists = [...parallelStitches, ...contourStitches];
  }

  drawPoint(ctx, x, y, color, transform) {
    ctx.beginPath();
    const radius = 5 / transform.scale;
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  }

  renderActivePoints(ctx, transform) {
    if (this.x !== undefined && this.y !== undefined) {
      this.drawPoint(ctx, this.x, this.y, 'red', transform);
      this.drawPoint(ctx, this.x + this.size, this.y + this.size, 'red', transform);
    }
  }

  /**
   * NEW: Checks if a point is inside the square or near its edges.
   */
  isPointInside(px, py, transform) {
    const tolerance = 5 / transform.scale;

    const minX = this.x;
    const maxX = this.x + this.size;
    const minY = this.y;
    const maxY = this.y + this.size;

    const isInside = (px >= minX && px <= maxX && py >= minY && py <= maxY);

    // Check if the point is near any edge
    const onHorizontalEdge = (px >= minX - tolerance && px <= maxX + tolerance) &&
      (Math.abs(py - minY) < tolerance || Math.abs(py - maxY) < tolerance);

    const onVerticalEdge = (py >= minY - tolerance && py <= maxY + tolerance) &&
      (Math.abs(px - minX) < tolerance || Math.abs(px - maxX) < tolerance);

    return isInside || onHorizontalEdge || onVerticalEdge;
  }

  setIsDrawing(dr) { this.m_isDrawing = dr }

  isDrawing() {
    return this.m_isDrawing
  }

  isComplete() {
    return this.m_hasSecondPoint;
  }

  AddPoint(point) {
    this.x = point.x;
    this.y = point.y;
    this.m_isDrawing = true;
    this.m_hasSecondPoint = false;
    return false;
  }

  AddSecondPoint(point) {
    // Handle negative drag (bottom-right to top-left) by swapping corners
    const dx = point.x - this.x;
    const dy = point.y - this.y;
    this.size = Math.max(Math.abs(dx), Math.abs(dy));

    // Ensure minimum size of 1
    if (this.size <= 0) this.size = 1;

    // Adjust origin if dragging backwards
    if (dx < 0) {
      this.x = point.x;
    }
    if (dy < 0) {
      this.y = point.y;
    }

    this.m_hasSecondPoint = true;
    this.regenerateStitches(); // Generate stitches when shape is completed
    return false;
  }

  static renderPreview(ctx, x1, y1, x2, y2, color, transform) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const size = Math.max(Math.abs(dx), Math.abs(dy));
    let x = x1;
    let y = y1;
    if (dx < 0) {
      x = x1 - size;
    }
    if (dy < 0) {
      y = y1 - size;
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, size, size);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1 / transform.scale;
    ctx.stroke();
    ctx.restore();
  }

  // Handle mouse events (e.g., resizing or moving the square)
  handleMouseEvent(event) {
    // Placeholder for mouse event handling logic
    console.log('Mouse event:', event);
  }
}
